import { Box, LinearProgress } from "@mui/material";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { isForbiddenError } from "../../../utils/Functions";
import NotFound, { ErrorMessage } from "../../saps/components/display/NotFound";
import AppointmentAlerts from "./components/AppointmentAlerts";
import AppointmentActions from "./components/actions/AppointmentActions";
import EditAppointmentButton from "./components/actions/EditAppointmentButton";
import AppointmentBanner from "./components/banner/AppointmentBanner";
import AppointmentTabs from "./components/tabs/AppointmentTabs";
import useAppointmentGeneral from "./hooks/useAppointmentGeneral";

/**
 * Single appointment view. Refetches on every visit, since the pet and its
 * owner are edited on other pages. Users without access to the appointment
 * are sent back to the previous page.
 */
export default function AppointmentView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useAppointmentGeneral({
    refetchOnMount: "always",
  });
  const isForbidden = isForbiddenError(error);

  useEffect(() => {
    if (isForbidden) navigate(-1);
  }, [isForbidden, navigate]);

  if (isLoading) {
    return (
      <PageContentWrapper>
        <LinearProgress />
      </PageContentWrapper>
    );
  } else if (error) {
    if (isForbidden) return null;
    return (
      <PageContentWrapper>
        <ErrorMessage
          title={t("ErrorFetchingAppointmentData")}
          subtitle={t("SomethingWentWrong")}
        />
      </PageContentWrapper>
    );
  } else if (!data?.appointment) {
    return <NotFound title={t("NoAppointmentFound")} />;
  }

  const { appointment } = data;

  return (
    <PageContentWrapper>
      <Box data-testid="appointment.details.loaded">
        <AppointmentAlerts appointment={appointment} />
        <AppointmentBanner
          appointment={appointment}
          actions={
            <Fragment>
              <EditAppointmentButton appointment={appointment} />
              <AppointmentActions appointment={appointment} />
            </Fragment>
          }
        />
        <AppointmentTabs appointment={appointment} />
      </Box>
    </PageContentWrapper>
  );
}
